import {useEffect, useState, useMemo} from "react"
import useAnalyticsStore from "../../../../store/analyticsStore"
import useHomeStore from "../../../../store/homeStore"
import {useTimeSeriesQuery} from "../../../../queries/useTimeseriesQuery";
import {useRosePlotQuery} from "../../../../queries/useRosePlotQuery";
import {useExtractionQuery} from "../../../../queries/useExtractionQuery";
import {useMetadataQuery} from "../../../../queries/useMetadataQuery";
import {DEFAULT_ERROR_MESSAGE} from "../../../../api/Api";

interface UseAnalyticsDataParams {
  previewDatasetId: string
  coordinates: number[]
  pointName: string
  gridCellSize: string
  waterDepth: string
  point?: any
  setDatasetIsOfflineWarningVisible: (visible: boolean) => void
  isOffline: boolean
  isFwe?: boolean
  fwePointName?: string
}

export const useBasicAnalyticsData = (params: UseAnalyticsDataParams, {enabled = false}: {enabled?: boolean}) => {
  const [timeSeriesPreview, setTimeSeriesPreview] = useState<any>(null)
  const [timeSeriesPreviewError, setTimeSeriesPreviewError] = useState('')
  const [scatterPlotPreview, setScatterPlotPreview] = useState<any>(null)
  const [scatterPlotPreviewError, setScatterPlotPreviewError] = useState('')
  const [rosePlotPreview, setRosePlotPreview] = useState<any>(null)
  const [rosePlotPreviewError, setRosePlotPreviewError] = useState('')
  const [analyticsDefinition, setAnalyticsDefinition] = useState('')
  const [defaultRosePlotDirection, setDefaultRosePlotDirection] = useState(1)

  const setEnrichedMetadata = useAnalyticsStore(state => state.setEnrichedMetadata)
  const previewAnalytics = useAnalyticsStore(state => state.previewAnalytics)
  const setPreviewAnalytics = useAnalyticsStore(state => state.setPreviewAnalytics)
  const timeStepInterval = useHomeStore(state => state.timeStepInterval)

  const resetState = () => {
    setRosePlotPreviewError('')
    setTimeSeriesPreviewError('')
    setTimeSeriesPreview(null)
    setRosePlotPreview(null)
    setScatterPlotPreview(null)
    setScatterPlotPreviewError('')
  }

  const pointName = params.isFwe ? params?.fwePointName ?? params.pointName : params.pointName

  const metadataQuery = useMetadataQuery(params.previewDatasetId, true)

  const extractionQuery = useExtractionQuery(
    params.previewDatasetId,
    params.coordinates,
    pointName,
    params.gridCellSize,
    params.waterDepth,
    timeStepInterval,
    metadataQuery.data,
    true
  )

  const orderId = useMemo(() => {
    return extractionQuery.data?.find((item) => item.CoordinatesString === params.coordinates.join(',') && item.DatasetId === params.previewDatasetId)?.OrderId
  }, [extractionQuery.data])


  const timeSeriesQuery = useTimeSeriesQuery(
    params.previewDatasetId,
    params.coordinates,
    pointName,
    timeStepInterval,
    metadataQuery.data,
    orderId,
    enabled
  )

  const rosePlotQuery = useRosePlotQuery(
    params.previewDatasetId,
    params.coordinates,
    pointName,
    timeStepInterval,
    metadataQuery.data,
    timeSeriesQuery.data?.data,
    timeSeriesQuery.isSuccess || timeSeriesQuery.isError,
    orderId,
    enabled
  )

  useEffect(() => {
    if (metadataQuery.data) {
      if (metadataQuery.data.IsOffline) {
        params.setDatasetIsOfflineWarningVisible(true)
        return
      }

      if (metadataQuery?.data.AnalyticsDefinition) {
        setAnalyticsDefinition(metadataQuery.data.AnalyticsDefinition)
      }
    }
  }, [metadataQuery?.data])

  useEffect(() => {
    if(params.isOffline){
      setTimeSeriesPreview(null)
      setTimeSeriesPreviewError('Dataset is offline, please contact administrator')
      setRosePlotPreview(null)
      setRosePlotPreviewError('Dataset is offline, please contact administrator')
      setScatterPlotPreview(null)
      setScatterPlotPreviewError('Dataset is offline, please contact administrator')
      }
  }, [params.isOffline])

  useEffect(() => {
    if (timeSeriesQuery.data && !params.isOffline) {
      setTimeSeriesPreview(null)
      setTimeSeriesPreviewError('')
      setScatterPlotPreview(null)
      setScatterPlotPreviewError('')
      if (timeSeriesQuery.data.success) {
        setTimeSeriesPreview(timeSeriesQuery.data.data)
        setScatterPlotPreview(timeSeriesQuery.data.data)
      } else {
        setTimeSeriesPreviewError(timeSeriesQuery.data.error)
        setScatterPlotPreviewError(timeSeriesQuery.data.error)
      }
    }
  }, [timeSeriesQuery.data, params.previewDatasetId, params.isOffline])

  useEffect(() => {
    if (rosePlotQuery.data) {
      setDefaultRosePlotDirection(1)
      setRosePlotPreview(null)
      setRosePlotPreviewError('')
      if (rosePlotQuery.data.direction) {
        setDefaultRosePlotDirection(rosePlotQuery.data.direction)
      }

      if (!rosePlotQuery.data.success) {
        setRosePlotPreviewError(rosePlotQuery.data.error || DEFAULT_ERROR_MESSAGE)
      } else {
        setRosePlotPreview(rosePlotQuery.data.data)

        if (timeSeriesQuery.data?.success) {
          setPreviewAnalytics([
            ...previewAnalytics,
            {
              coordinates: params.coordinates,
              data: timeSeriesQuery.data.data!.Result,
              datasetId: params.previewDatasetId,
              rosePlotPreviewdata: rosePlotQuery.data.data,
            },
          ])
        }
      }
    }
  }, [rosePlotQuery.data, timeSeriesQuery.data, params.previewDatasetId, params.coordinates])

  useEffect(() => {
    if (extractionQuery.data) {
      setEnrichedMetadata(extractionQuery.data)
    }
  }, [extractionQuery.data, setEnrichedMetadata])


  return {
    timeSeriesPreview,
    timeSeriesPreviewError,
    rosePlotPreview,
    rosePlotPreviewError,
    analyticsDefinition,
    defaultRosePlotDirection,
    setTimeSeriesPreview,
    scatterPlotPreview,
    scatterPlotPreviewError,
    setRosePlotPreview,
    setScatterPlotPreview,

    loadingInitialAnalytics: timeSeriesQuery.isLoading || metadataQuery.isLoading || rosePlotQuery.isLoading || extractionQuery.isLoading,
    isExtractionLoading: metadataQuery.isLoading,
    isMetadataLoading: metadataQuery.isLoading,
    isTimeSeriesLoading: timeSeriesQuery.isLoading,
    isRosePlotLoading: rosePlotQuery.isLoading,
    isEnrichedMetadataLoading: metadataQuery.isLoading,

    metadataError: metadataQuery.error,
    timeSeriesError: timeSeriesQuery.error,
    rosePlotError: rosePlotQuery.error,
    enrichedMetadataError: metadataQuery.error,

    isMetadataSuccess: metadataQuery.isSuccess,
    isTimeSeriesSuccess: timeSeriesQuery.isSuccess,
    isRosePlotSuccess: rosePlotQuery.isSuccess,
    isEnrichedMetadataSuccess: metadataQuery.isSuccess,

    resetState,
  }
}

